import { useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Popper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import { PERIOD_PRESETS, matchPreset } from './periodPresets';

// Modeled after the WorkTimeReportToolbar DateRangeTrigger.
// Compact trigger showing only the current label + caret; popper offers presets + custom range.

const formatRange = (from, to, t) => {
  if (!from && !to) return t('Total');
  const fmt = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y.slice(2)}`;
  };
  return `${fmt(from)} – ${fmt(to)}`;
};

const PeriodSelector = ({ from, to, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const matched = matchPreset(from, to);
  const label = matched ? t(matched.labelKey) : formatRange(from, to, t);

  const handlePresetClick = (preset) => {
    const r = preset.get();
    onChange({ key: preset.key, from: r.from, to: r.to });
    setOpen(false);
  };

  const handleCustom = (nextFrom, nextTo) => {
    onChange({ key: 'custom', from: nextFrom, to: nextTo });
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition-colors"
        sx={{
          '&:hover': { backgroundColor: colors.card },
        }}
      >
        <CalendarTodayIcon sx={{ fontSize: 14, opacity: 0.6 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, color: colors.text }}>
          {label}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: 16, opacity: 0.5 }} />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        sx={{ zIndex: 9999, paddingTop: '6px' }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            className="rounded-lg backdrop-blur-lg p-2 flex flex-col gap-2 min-w-[240px]"
            sx={{ backgroundColor: colors.cardSolid, boxShadow: 3 }}
          >
            <Box className="flex flex-col">
              {PERIOD_PRESETS.map((preset) => {
                const isActive = matched?.key === preset.key;
                return (
                  <Box
                    key={preset.key}
                    onClick={() => handlePresetClick(preset)}
                    className="px-2 py-1 rounded cursor-pointer transition-colors"
                    sx={{
                      backgroundColor: isActive ? `${colors.info}22` : 'transparent',
                      color: isActive ? colors.info : colors.text,
                      fontWeight: isActive ? 600 : 400,
                      '&:hover': { backgroundColor: colors.card },
                    }}
                  >
                    <Typography variant="body2">{t(preset.labelKey)}</Typography>
                  </Box>
                );
              })}
            </Box>
            <Box
              className="pt-2 flex flex-col gap-2 border-t"
              sx={{ borderColor: colors.card }}
            >
              <Typography variant="caption" sx={{ opacity: 0.7, paddingLeft: 0.5 }}>
                {t('Custom range')}
              </Typography>
              <Box className="flex items-center gap-2">
                <TextField
                  type="date"
                  size="small"
                  value={from ?? ''}
                  onChange={(e) => handleCustom(e.target.value || null, to)}
                  sx={{ flex: 1, '& input': { fontSize: '0.78rem', padding: '6px 8px' } }}
                />
                <Typography variant="caption" sx={{ opacity: 0.5 }}>
                  –
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={to ?? ''}
                  onChange={(e) => handleCustom(from, e.target.value || null)}
                  sx={{ flex: 1, '& input': { fontSize: '0.78rem', padding: '6px 8px' } }}
                />
              </Box>
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default PeriodSelector;
